import BlueprintUtils from '../blueprintUtils.js'
import RichPlaceholderCompiler from '../compilers/RichPlaceholderCompiler'
import { WIDGET_PREVIEW_SIZE } from '../constants'

function parseElementStyle(props) {
  const { widget, isHyperlinkEnabled, zModifier, isRenderingInsideLayerTool, isRenderingInsideAceEditor } = props
  let widgetLayout = widget.layout || {}
  let widgetBaseStyle = widget.baseStyle ? JSON.parse(JSON.stringify(widget.baseStyle)) : {}
  let animationTypeMap = {}
  // In preparation for multiple animations, animation fill mode is converted to 'forwards' instead of 'both'.
  // This is because the 'both' fill mode does not work with multiple animations. This then requires us to set
  // the position of the actual widget to the starting point of the animation otherwise the widget will become visible
  // in the end position of the animation and not the starting position.
  if (widget.animations?.length && !isRenderingInsideAceEditor) {
    widget.animations.forEach(a => {
      if (
        (a.type !== 'transition' && animationTypeMap[a.type]) ||
        (a.type === 'transition' && animationTypeMap[a.targetStyle.name])
      ) {
        return
      }

      switch (true) {
        case a.type === 'move' || a.type === 'resize':
          animationTypeMap['move'] = true
          animationTypeMap['resize'] = true
          widgetLayout = {
            ...widgetLayout,
            x: a.animationStates[0].layout.x,
            y: a.animationStates[0].layout.y,
            width: a.animationStates[0].layout.width,
            height: a.animationStates[0].layout.height,
          }
          break
        case a.type === 'transition':
          animationTypeMap[a.targetStyle.name] = true
          widgetBaseStyle[a.targetStyle.name] = a.animationStates[0].transition.value
          break
        case a.type === 'rotation':
          animationTypeMap[a.type] = true
          widgetLayout = {
            ...widgetLayout,
            zRotation: a.animationStates[0].rotation.value,
          }
          widgetBaseStyle.horizontal = a.animationStates[0].horizontal.value
          widgetBaseStyle.vertical = a.animationStates[0].vertical.value
          break
      }
    })
  }

  // if inside ace editor, set to current base style, not starting point of animation
  if (isRenderingInsideAceEditor) {
    widgetBaseStyle = widget.baseStyle
  }
  const scale =
    isRenderingInsideLayerTool && WIDGET_PREVIEW_SIZE / Math.max(widgetLayout.width, widgetLayout.height) < 1
      ? `scale(${WIDGET_PREVIEW_SIZE / Math.max(widgetLayout.width, widgetLayout.height)})`
      : ''

  const st = {
    position: 'absolute',
    overflow: 'hidden',

    // Allow link-less widgets to be non-interactive, so that user could
    // interact with underlying links
    'pointer-events': isHyperlinkEnabled && !widget.href && 'none',

    'box-sizing': 'border-box',
    width: `${widgetLayout.width}px`,
    height: `${widgetLayout.height}px`,

    top: isRenderingInsideLayerTool ? '' : `${widgetLayout.y}px`,
    left: isRenderingInsideLayerTool ? '' : `${widgetLayout.x}px`,

    'z-index': widgetLayout.z + zModifier,

    background: widgetBaseStyle.background || 'transparent',
    color: widgetBaseStyle.color,
    // 0 values do not get rendered so we use percent for 0 values
    opacity: widgetBaseStyle.opacity === 0 ? '0%' : widgetBaseStyle.opacity || 1,

    'font-family': widgetBaseStyle['font-family'],
    'font-size': `${widgetBaseStyle['font-size']}px`,
    // 0 values do not get rendered so we use percent for 0 values
    'line-height': widgetBaseStyle['line-height'] === 0 ? '0%' : widgetBaseStyle['line-height'] || 1,
    'font-style': widgetBaseStyle['font-style'],
    'font-weight': widgetBaseStyle['font-weight'],

    'text-align': widgetBaseStyle['text-align'],
    // hide widget if visibility off and widget is inside template (does not hide widget from actual ad)
    // strict false check is necessary as isLayerVisible undefined should default to true (DO NOT CHANGE to falsy !widget.isLayerVisible check)
    // widget visibility by default should be true
    display:
      widget.isLayerVisible === false && isRenderingInsideAceEditor && !isRenderingInsideLayerTool ? 'none' : 'flex',
    'flex-flow': 'column nowrap',
    'justify-content': widgetBaseStyle['justify-content'],

    'text-decoration': widgetBaseStyle['text-decoration'],

    'border-width': `${widgetBaseStyle['border-width'] || 0}px`,
    'border-radius': `${widgetBaseStyle['border-radius'] * 100 || 0}px`,
    'border-style': widgetBaseStyle['border-style'],
    'border-color': widgetBaseStyle['border-color'],

    transform: isRenderingInsideLayerTool
      ? `${scale}`
      : `rotate(${widgetLayout.zRotation || 0}deg) rotateY(${widgetBaseStyle.horizontal ||
          0}deg) rotateX(${widgetBaseStyle.vertical || 0}deg)`,
  }

  return st
}

function parseTextStyle(widget) {
  const widgetBaseStyle = widget.baseStyle || {}

  return {
    'white-space': widgetBaseStyle['white-space'],
  }
}

function escape(rawString) {
  return rawString.replace(/[<>]/g, substr => {
    return (
      {
        '<': '&lt;',
        '>': '&gt;',
      }[substr] || substr
    )
  })
}

function withDivContainer(sourceElement, $h) {
  //context was bound to this
  const props = this.props
  //account for any native events the template editor is trying to apply (ie mouseover, contextmenu)
  const nativeOn = (this.data && this.data.nativeOn) || {}

  // inventoryIndex from prop contains correct inventory index information
  // apply to widget.rule before compiling
  let widgetRule = this.props.widget.rule
  if (widgetRule) {
    widgetRule.inventoryIndex = this.props.inventoryIndex
  }

  return $h(
    'div',
    {
      on: {
        ...nativeOn,
      },
      style: parseElementStyle(props),
      RHStyles: widgetRule ? escape(this.props.contentCompiler.compile(widgetRule)) : '',
      attrs: {
        'data-adacadotransitiontype': 'transition',
        'widget-id': `widget${props.widget.id}`,
        'data-adacadopropname': props.widget.value[0].content.name
          ? `${props.widget.value[0].content.name}${props.widget.id}`
          : false,
        class: props.animationClass,
      },
    },
    [sourceElement]
  )
}

function withAnchorContainer(sourceElement, $h) {
  // Wrap widget in an anchor tag if there is a value for href property
  if (this.isHyperlinkEnabled && this.widget.href) {
    // convert older widget hrefs into RP hrefs
    if (this.widget.meta?.widgetVersion === 1 && typeof this.widget.href === 'string') {
      // set href defaultValue to null if empty string
      this.widget.href = BlueprintUtils.create.richPlaceholderHref(this.widget.href ? this.widget.href : null)
    }
    // inventoryIndex from prop contains correct inventory index information
    // apply to widget.href before compiling html
    let widgetHref = JSON.parse(JSON.stringify(this.widget.href))
    widgetHref.inventoryIndex = this.inventoryIndex
    return $h(
      'a',
      {
        attrs: {
          // href attributes of widget are completely controlled by the end-user,
          // so it is important to sanitize the inputs in all cases.
          // FIXME: we are performing bare minimum checks that replaces `<` and `>`
          // Due to lack of support from vue(see  https://github.com/vuejs/vue/issues/8657),
          // we would have to incorporate other libraries for a complete sanitization logic
          href: escapeLinkAttribute(this.contentCompiler.compile(widgetHref, { isURL: true })),
          target: '_blank',
        },
      },
      [sourceElement]
    )
  } else {
    // pointer-events attribute will be set to "none" when isHyperlinkEnabled is true but widget.href is not defined
    // this allows the widget to inherit link behavior from the background
    return sourceElement
  }
}

function escapeLinkAttribute(rawString) {
  return rawString.replace(/[<>]/g, substr => {
    return (
      {
        '<': '&lt;',
        '>': '&gt;',
      }[substr] || substr
    )
  })
}

// get advertiser data
function getAdvertiserAddress(advertiser, widget) {
  if (!advertiser?.Properties?.Address) return 'Advertiser Address'

  // address format when auto-formatting is on
  // [StreetAddress1]
  // [StreetAddress2]
  // [City], [StateProvince], [Country]
  // [ZipPostal]

  // address format when auto-formatting is off
  // [StreetAddress1] [StreetAddress2], [City], [StateProvince], [Country] [ZipPostal]
  // e.g. 999 Canada Pl, Vancouver, BC, CANADA V6C 3T4

  let result = ''
  const isAutoFormatAddress = widget.baseStyle['white-space'] === 'pre-line'
  const advertiserAddress = advertiser.Properties.Address
  const baseAdvertiserAddressArray = [
    'StreetAddress1',
    'StreetAddress2',
    'City',
    'StateProvince',
    'Country',
    'ZipPostal',
  ]

  baseAdvertiserAddressArray.forEach(x => {
    if (!advertiserAddress[x]) return

    if (
      (x === 'StreetAddress1' && !advertiserAddress['StreetAddress2'] && !isAutoFormatAddress) ||
      (x === 'StreetAddress2' && !isAutoFormatAddress) ||
      x === 'City' ||
      x === 'StateProvince'
    ) {
      result += `${advertiserAddress[x]}, `
    } else {
      result += `${advertiserAddress[x]}\n`
    }
  })
  return result
}

export default {
  functional: true,
  props: {
    widget: {
      type: Object,
      required: true,
    },
    animationClass: {
      type: String,
      default: '',
    },
    isRenderingInsideAceEditor: {
      type: Boolean,
      default: false,
    },
    isRenderingInsideLayerTool: {
      type: Boolean,
      default: false,
    },
    isHyperlinkEnabled: Boolean,
    // CanvasTranspiler will pass this a prop where the RichPlaceholderCompiler's mode is set to 'placeholder'
    // which generates richplaceholders in the output html as opposed to just the default value (for viewing in Ace Editor)
    contentCompiler: {
      type: Object,
      default() {
        return new RichPlaceholderCompiler()
      },
    },
    zModifier: {
      type: Number,
      default: 0,
    },
    inventoryIndex: {
      type: Number,
      default: 0,
    },
    themeStaticData: {
      type: Array,
      default() {
        return []
      },
    },
    currentAdvertiser: {
      type: Object,
      default() {
        return undefined
      },
    },
  },
  render($h, context) {
    const { widget: w, contentCompiler, inventoryIndex, themeStaticData = [], currentAdvertiser } = context.props
    const widget = JSON.parse(JSON.stringify(w))
    const widgetStaticData = themeStaticData.find(d => d.type == widget.type && d.name == widget.value[0].content.name)
    const textElements = (widget.value || []).map(textToken => {
      // Inject inventoryIndex value for rich placeholder compiler
      // Remember, widget.value.content is sometimes and OBJECT, sometimes a STRING - FIXME
      if (textToken && typeof textToken.content == 'object') {
        if (textToken.content.isAdvertiserData && currentAdvertiser) {
          switch (textToken.content.name) {
            case 'advertiserAddress':
              textToken.content.defaultValue = getAdvertiserAddress(currentAdvertiser, widget)
              break
            case 'advertiserName':
              textToken.content.defaultValue = currentAdvertiser.Name || ''
              break
            case 'advertiserDBAName':
              textToken.content.defaultValue = currentAdvertiser?.Properties?.DBAName || ''
              break
            case 'advertiserEmail':
              textToken.content.defaultValue = currentAdvertiser?.Properties?.Email || ''
              break
            case 'advertiserPhone':
              textToken.content.defaultValue = currentAdvertiser?.Properties?.Phone || ''
          }
        } else {
          textToken.content.defaultValue = widgetStaticData ? widgetStaticData.value : textToken.content.defaultValue
        }
        textToken.content.inventoryIndex = inventoryIndex
      }
      if (widget.href && typeof widget.href == 'object') {
        widget.href.inventoryIndex = inventoryIndex
      }

      return $h('span', {
        style: parseTextStyle(widget),
        domProps: {
          innerHTML: escape(contentCompiler.compile(textToken.content)),
        },
      })
    })
    // decorate with div, and then finally anchor
    return withAnchorContainer.call(context.props, withDivContainer.call(context, textElements, $h), $h)
  },
}
